import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { clearActiveProfile } from '../utils/profile';
import './account-preferences.css';
import ConfirmDialog from './ConfirmDialog';
const deletionDraftKey = 'deenlit-deletion-dialog';
const deletionAccountId = () => {
  try { return JSON.parse(localStorage.getItem('adeeb-user') || 'null')?._id || ''; }
  catch { return ''; }
};
const readDeletionDraft = () => {
  try {
    const draft = JSON.parse(sessionStorage.getItem(deletionDraftKey) || 'null');
    if (draft?.accountId && draft.accountId === deletionAccountId() && draft.expiresAt > Date.now()) return draft;
    sessionStorage.removeItem(deletionDraftKey);
  } catch { /* storage may be unavailable */ }
  return null;
};
const clearDeletionDraft = () => {
  try { sessionStorage.removeItem(deletionDraftKey); } catch { /* ignore */ }
};
export default function AccountPreferences() {
  const [settings, setSettings] = useState(null), [open, setOpen] = useState(() => !!readDeletionDraft()?.open), [confirmation, setConfirmation] = useState(() => readDeletionDraft()?.confirmation || ''), [password, setPassword] = useState(''), [code, setCode] = useState(() => readDeletionDraft()?.code || ''), [message, setMessage] = useState(''), [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    try { sessionStorage.setItem(deletionDraftKey, JSON.stringify({ accountId: deletionAccountId(), open: true, confirmation, code, expiresAt: Date.now() + 10 * 60 * 1000 })); }
    catch { /* storage may be unavailable */ }
  }, [open, confirmation, code]);
  useEffect(() => { axios.get('/api/account').then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (settings && window.location.hash === '#email-preferences') {
      document.getElementById('email-preferences')?.scrollIntoView({ block: 'center' });
    }
  }, [settings]);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  const changeNewsletter = async (subscribed) => {
    setEmailBusy(true); setEmailError('');
    const previous = settings.newsletter;
    setSettings(current => ({ ...current, newsletter: subscribed }));
    try {
      await axios.patch('/api/account/newsletter', { subscribed });
      setSettings(current => ({ ...current, newsletter: subscribed }));
    } catch { setSettings(current => ({ ...current, newsletter: previous })); setEmailError('Could not update your preference. Please try again.'); }
    finally { setEmailBusy(false); }
  };
  const requestCode = async () => {
    setBusy(true);
    try { await axios.post('/api/account/deletion-code'); setMessage('Check your email for the confirmation code.'); }
    catch (e) { setMessage(e.response?.data?.message || 'Could not send code.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (confirmation !== 'DELETE') { setMessage('Type DELETE in the confirmation box first.'); return; }
    if (settings.provider === 'local' && !password) { setMessage('Enter your current password to continue.'); return; }
    if (settings.provider !== 'local' && !code) { setMessage('Enter the confirmation code from your email to continue.'); return; }
    setBusy(true); setMessage('');
    try {
      await axios.delete('/api/account', { data: { confirmation, password, code } });
      clearDeletionDraft();
      ['adeeb-token', 'adeeb-user', 'adeeb-admin-token'].forEach(k => localStorage.removeItem(k)); clearActiveProfile(); window.dispatchEvent(new Event('adeeb-auth-change')); window.location.assign('/');
    } catch (err) { setMessage(err.response?.data?.message || 'Could not delete account.'); }
    finally { setBusy(false); }
  };
  if (!settings) return null;
  const close = () => { if (!busy) { clearDeletionDraft(); setOpen(false); setConfirmation(''); setPassword(''); setCode(''); setMessage(''); } };
  return <section className="account-preferences" aria-label="Account preferences">
    <div id="email-preferences" className="sub-card account-email-card">
      <h2>Keep up to date</h2>
      <label className="account-email-option"><input type="checkbox" checked={!!settings.newsletter} disabled={emailBusy} onChange={e => changeNewsletter(e.target.checked)} /><span>Email me about new book releases</span></label>
      <p>Get an email when a new book arrives. Untick this option or unsubscribe from any release email to stop.</p>
      {emailError && <p role="alert">{emailError}</p>}
    </div>
    <div className="account-delete"><button className="sub-secondary account-delete-confirm" type="button" onClick={() => setOpen(true)}>Delete my account</button></div>
    <ConfirmDialog open={open} title="Delete your account?" tone="danger" busy={busy} cardClassName="account-delete-dialog"
      body="Your account and all its reader profiles, reading progress and preferences will be archived, not permanently erased. Any subscription is cancelled immediately. Payment records held by Stripe remain separate. You can contact support to request restoration in future."
      cancelLabel="Keep my account" confirmLabel="Yes, delete account"
      onClose={close} onConfirm={remove}>
      <div className="account-delete-content">
        <div className="account-delete-form">
          <label>Type DELETE to confirm<input required disabled={busy} value={confirmation} onChange={e => setConfirmation(e.target.value)} /></label>
          {settings.provider === 'local' ? <label>Current password<input required disabled={busy} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} /></label> : <><button className="sub-secondary" type="button" disabled={busy} onClick={requestCode}>Email confirmation code</button><label>Confirmation code<input required disabled={busy} inputMode="numeric" value={code} onChange={e => setCode(e.target.value)} /></label></>}
        </div>
        {message && <p role="alert">{message}</p>}
      </div>
    </ConfirmDialog>
  </section>;
}
